import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { DocumentHandling, DocumentType } from '../model/inko-backend.model'

/// Model

export interface DokumentenScannerStateModel {
  state?: {
    params: {
      type: 'phm' | 'himi'
      id: string
    }
    missingDocuments: DocumentType[]
    documents: (
      | {
          type: 'images' | 'pdf'
          files: File[]
        }
      | undefined
    )[]
    documentHandling?: DocumentHandling
  }
}

/// Actions
export class ResetDokumentenScanner {
  static readonly type = '[DokumentenScanner] Reset'
}
export class InitDokumentenScanner {
  static readonly type = '[DokumentenScanner] Init'
  constructor(public readonly payload: { type: 'phm' | 'himi'; id: string }) {}
}
export class SetMissingDocuments {
  static readonly type = '[DokumentenScanner] Set Missing Documents'
  constructor(public readonly payload: DocumentType[]) {}
}
export class SetPdfDocument {
  static readonly type = '[DokumentenScanner] Set PDF Document'
  constructor(
    public readonly index: number,
    public readonly file: File | null,
  ) {}
}
export class SetImagesDocument {
  static readonly type = '[DokumentenScanner] Set Images Document'
  constructor(
    public readonly index: number,
    public readonly files: File[],
  ) {}
}
export class SetDocumentHandling {
  static readonly type = '[DokumentenScanner] Set Document Handling'
  constructor(public readonly payload: DocumentHandling) {}
}

/// State

@Injectable()
@State<DokumentenScannerStateModel>({
  name: 'dokumentenScanner',
  defaults: { state: undefined },
})
export class DokumentenScannerState {
  @Selector()
  static state({ state }: DokumentenScannerStateModel) {
    return state
  }
  @Selector()
  static params({ state }: DokumentenScannerStateModel) {
    return state?.params
  }
  @Selector()
  static missingDocuments({ state }: DokumentenScannerStateModel) {
    return state?.missingDocuments
  }
  @Selector()
  static documents({ state }: DokumentenScannerStateModel) {
    return state?.documents
  }
  @Selector()
  static handlingItem({ state }: DokumentenScannerStateModel) {
    return state?.documentHandling
  }

  @Action(ResetDokumentenScanner)
  reset({ patchState }: StateContext<DokumentenScannerStateModel>) {
    patchState({ state: undefined })
  }

  @Action(InitDokumentenScanner)
  init({ patchState }: StateContext<DokumentenScannerStateModel>, { payload }: InitDokumentenScanner) {
    patchState({
      state: {
        params: {
          type: payload.type,
          id: payload.id,
        },
        missingDocuments: [],
        documents: [],
      },
    })
  }

  @Action(SetMissingDocuments)
  setMissingDocuments({ getState, patchState }: StateContext<DokumentenScannerStateModel>, { payload }: SetMissingDocuments) {
    const currentState = getState().state
    if (currentState) {
      patchState({
        state: {
          ...currentState,
          missingDocuments: payload,
          documents: payload.map(() => undefined), // Initialize documents with undefined for each missing document
        },
      })
    } else {
      console.warn('State is not initialized, cannot set missing documents.')
    }
  }

  @Action(SetPdfDocument)
  setPdfDocument({ getState, patchState }: StateContext<DokumentenScannerStateModel>, { index, file }: SetPdfDocument) {
    const currentState = getState().state
    if (currentState && currentState.documents) {
      const updatedDocuments = [...currentState.documents]
      updatedDocuments[index] = file ? { type: 'pdf', files: [file] } : undefined
      patchState({
        state: {
          ...currentState,
          documents: updatedDocuments,
        },
      })
    } else {
      console.warn('State is not initialized or documents array is missing.')
    }
  }

  @Action(SetImagesDocument)
  setImagesDocument({ getState, patchState }: StateContext<DokumentenScannerStateModel>, { index, files }: SetImagesDocument) {
    const currentState = getState().state
    if (currentState && currentState.documents) {
      const updatedDocuments = [...currentState.documents]
      updatedDocuments[index] = files.length > 0 ? { type: 'images', files } : undefined
      patchState({
        state: {
          ...currentState,
          documents: updatedDocuments,
        },
      })
    } else {
      console.warn('State is not initialized or documents array is missing.')
    }
  }

  @Action(SetDocumentHandling)
  setDocumentHandling({ getState, patchState }: StateContext<DokumentenScannerStateModel>, { payload }: SetDocumentHandling) {
    const currentState = getState().state
    if (currentState) {
      patchState({
        state: {
          ...currentState,
          documentHandling: payload,
        },
      })
    } else {
      console.warn('State is not initialized, cannot set document handling.')
    }
  }
}
